import React, { useState, useEffect } from "react";
import { createTweet } from "../../services/tweetService";
import { fetchBlogContent } from "../../services/extractContent";
import { createCheckout } from "../../services/planService";
import { openPaddleCheckoutForTransaction } from "../../services/paddleService";
import { useLocation } from "react-router-dom";
import { useToast } from "../../provider/ToastContext";
import { Sparkles } from "lucide-react";

import UrlInput from "./URLInput";
import BlogContent from "./BlogContent";
import GeneratedPosts from "./GeneratedPosts";

const CreateTweet = ({ collapsed }) => {
  const [tweet, setTweet] = useState("");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [platform, setPlatform] = useState("Twitter");

  const [totalposts, setTotalPosts] = useState(2);
  const [isHashtag, setIsHashtag] = useState(1);

  const [generatedTweets, setGeneratedTweets] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);

  const location = useLocation();
  const { showToast } = useToast();

  useEffect(() => {
    if (location.state?.loginSuccess) {
      showToast("Logged in successfully!", "success");
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showToast]);

  useEffect(() => {
    const pendingCheckout = sessionStorage.getItem("pending_checkout");

    if (!pendingCheckout) {
      return;
    }

    let checkoutIntent;

    try {
      checkoutIntent = JSON.parse(pendingCheckout);
    } catch (error) {
      sessionStorage.removeItem("pending_checkout");
      return;
    }

    if (!checkoutIntent?.plan_id) {
      sessionStorage.removeItem("pending_checkout");
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    const startPendingCheckout = async () => {
      try {
        const token = localStorage.getItem("token"); // use your actual auth token key

        if (!token) {
          return;
        }

        const checkout = await createCheckout(
          checkoutIntent.plan_id,
          checkoutIntent.billing_cycle || "monthly",
        );

        if (!checkout?.transactionId) {
          throw new Error("Checkout transaction missing.");
        }

        await openPaddleCheckoutForTransaction(checkout.transactionId);

        sessionStorage.removeItem("pending_checkout");
      } catch (error) {
        console.error("Unable to start pending checkout:", error);

        sessionStorage.removeItem("pending_checkout");

        showToast("Unable to start checkout.", "error");
      }
    };

    startPendingCheckout();
  }, [showToast]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("checkout") === "success") {
      showToast(
        "Payment successful. Your plan will be activated shortly.",
        "success",
      );

      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [showToast]);

  /* -------- CLEANER -------- */
  const formatContent = (text) => {
    if (!text) return "";

    return text
      .replace(/<script[^>]*>.*?<\/script>/gi, "")
      .replace(/<style[^>]*>.*?<\/style>/gi, "")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<li>/gi, "• ")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  /* -------- FETCH BLOG -------- */
  const fetchBlog = async () => {
    if (!url.trim()) {
      showToast("Please enter a blog URL", "error");
      return;
    }

    showToast("Fetching blog content...", "loading");

    try {
      const response = await fetchBlogContent({ url, title });
      const cleaned = formatContent(response.data.content);

      setTweet(cleaned);

      showToast("Blog fetched successfully!", "success");
    } catch (err) {
      showToast("Failed to fetch blog. Paste manually.", "error");
    }
  };

  /* -------- GENERATE -------- */
  const createNewTweet = async () => {
    if (!tweet.trim()) {
      showToast("Blog content is empty", "error");
      return;
    }

    const data = {
      text: tweet,
      total_posts: totalposts,
      is_hashtag: Boolean(isHashtag),
      platform: platform,
    };

    showToast("Generating posts...", "loading");

    try {
      const response = await createTweet(data);

      let tweets = [];

      if (Array.isArray(response.data?.data)) {
        tweets = response.data.data;
      } else if (typeof response.data?.data === "string") {
        tweets = response.data.data.split(/\n\n+/);
      }

      setGeneratedTweets(tweets);

      showToast("Posts generated successfully!", "success");
    } catch (err) {
      showToast("Failed to generate posts", "error");
    }
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${
        collapsed ? "lg:ml-20" : "lg:ml-72"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-indigo-600" size={20} />
            <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
              AI Generator
            </span>
          </div>

          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Create Social Media Posts from Blogs
          </h1>

          <p className="text-sm text-gray-500 mt-2 max-w-xl">
            Instantly convert long-form content into engaging,
            platform-optimized posts.
          </p>
        </div>
        {/* URL INPUT */}
        <UrlInput url={url} setUrl={setUrl} fetchBlog={fetchBlog} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* BLOG CONTENT */}
          <BlogContent
            tweet={tweet}
            setTweet={setTweet}
            totalposts={totalposts}
            setTotalPosts={setTotalPosts}
            isHashtag={isHashtag}
            setIsHashtag={setIsHashtag}
            createNewTweet={createNewTweet}
            formatContent={formatContent}
            platform={platform}
            setPlatform={setPlatform}
          />

          {/* GENERATED POSTS */}
          <GeneratedPosts
            generatedTweets={generatedTweets}
            setGeneratedTweets={setGeneratedTweets}
            editingIndex={editingIndex}
            setEditingIndex={setEditingIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateTweet;
